<template>
  <!-- The POS code input form -->
  <q-item
    v-if="$register && !checkout && $q.screen.gt.xs"
    class="bg-responsive"
  >
    <q-item-section avatar>
      <q-avatar
        color="primary"
        text-color="white"
      >
        %
      </q-avatar>
    </q-item-section>
    <q-item-section>
      <q-item-label class="row items-center">
        <q-chip
          v-if="!viewOnly && !isCheckoutCodeInputActive"
          style="border-width: 0;"
          outline
          ripple
          icon="add"
          :label="$t('checkout.actions.addGiftCardVoucherPromoCode')"
          size="md"
          clickable
          data-test-id="cart-add-checkout-code"
          @click="isCheckoutCodeInputActive = true"
        />
        <q-chip
          v-else-if="!viewOnly"
          square
          outline
          style="border: none;"
        >
          <q-input
            v-model.trim="checkoutCodeUid"
            :label="computedInputLabel"
            :stack-label="false"
            :outlined="false"
            borderless
            autofocus
            hide-bottom-space
            :loading="checkoutCodeInputLoading"
            :error="!!errorMessage"
            data-test-id="pos-checkout-code-input"
            @update:model-value="findCheckoutCodeOnInput"
            @keydown.enter.prevent="findCheckoutCodeOnEnter"
            @focusout="isCheckoutCodeInputActive = checkoutCodeUid === '' ? false : isCheckoutCodeInputActive"
          />
        </q-chip>
        <q-chip
          v-for="voucher in order.uniqueAppliedVouchers"
          :key="voucher.typeName + voucher.id"
          square
          :removable="!viewOnly && voucher.canRemove"
          dense
          :color="voucher.color"
          text-color="white"
          :label="(voucher.typeName === $tc('voucher.voucher', 1) ? voucher.voucherType.name : voucher.name) + ( voucher.isReward ? ` (${voucher.voucherType.pointsCost})` : '')"
          size="md"
          @remove="voucher.autoApply = false; order.applyRedemptions()"
        />
      </q-item-label>
    </q-item-section>
    <q-item-section
      v-if="order.appliedVoucherAmount"
      side
    >
      <div class="text-subtitle2 col-auto text-red">
        -
        <i18n-n
          class="text-bold text-red"
          tag="span"
          :value="order.appliedVoucherAmount"
          format="currency"
          scope="global"
          data-test-id="cart-voucher-applied-value"
        />
      </div>
    </q-item-section>
    <voucher-package-dialog
      v-if="!viewOnly"
      ref="voucherPackageDialog"
      :order="order"
      destroy-on-close
      @add-voucher="voucher => $emit('add-voucher', voucher)"
      @add-voucher-package="voucherPackage => $emit('add-voucher-package', voucherPackage)"
    />
  </q-item>
  <!-- The non-POS (Kiosk, consumer) code input form -->
  <q-item
    v-else
    :class="{
      'q-px-sm': !checkout || isDrawer,
      'q-pa-none': checkout
    }"
  >
    <q-item-section>
      <q-item-label
        class="row"
        :class="{ 'bg-responsive-white': !isDrawer && checkout }"
      >
        <!-- Code input field -->
        <q-input
          v-if="!viewOnly"
          v-model.trim="checkoutCodeUid"
          :autofocus="autofocus"
          :label="computedInputLabel"
          :stack-label="false"
          :loading="checkoutCodeInputLoading"
          outlined
          :dense="isDrawer || $register"
          hide-bottom-space
          class="full-width"
          :error="!!errorMessage"
          @update:model-value="findCheckoutCodeOnInput"
          @keydown.enter.prevent="findCheckoutCodeOnEnter"
          @focusout="isCheckoutCodeInputActive = checkoutCodeUid === '' ? false : isCheckoutCodeInputActive"
        >
          <template #append>
            <!-- Apply button -->
            <q-btn
              color="secondary"
              :text-color="$isColorDark(currentSite.secondaryColor) ? 'white' : 'black'"
              dense
              :size="isDrawer || $register ? 'sm' : undefined"
              rounded
              class="q-px-sm"
              :label="$t('actions.apply')"
              :disable="checkoutCodeInputLoading"
              @click.stop="findCheckoutCodeOnEnter"
            />
          </template>
        </q-input>
      </q-item-label>
      <q-item-label class="row">
        <template
          v-for="voucher in order.uniqueAppliedVouchers"
        >
          <q-chip
            v-if="!voucher.isReward"
            :key="voucher.typeName + voucher.id"
            square
            :removable="!viewOnly && voucher.canRemove"
            dense
            :color="voucher.color"
            :text-color="textColor(voucher.color)"
            :label="(voucher.typeName === $tc('voucher.voucher', 1) ? voucher.voucherType.name : voucher.name) + ( voucher.isReward ? ` (${voucher.voucherType.pointsCost})` : '')"
            :size="isDrawer ? 'sm' : 'md'"
            @remove="voucher.autoApply = false; order.applyRedemptions()"
          />
        </template>
      </q-item-label>
    </q-item-section>
    <voucher-package-dialog
      v-if="!viewOnly"
      ref="voucherPackageDialog"
      :order="order"
      @add-voucher="voucher => $emit('add-voucher', voucher)"
      @add-voucher-package="voucherPackage => $emit('add-voucher-package', voucherPackage)"
    />
  </q-item>
</template>

<script>
import { Notify } from 'quasar'
import i18n from 'src/i18n'
import CheckoutCode, { CHECKOUT_CODE_ERROR_CODES, CHECKOUT_CODE_TYPES } from 'src/models/checkout-code/CheckoutCode'
import Voucher from 'src/models/voucher/Voucher'
import VoucherPackageDialog from 'src/modules/checkout/components/VoucherPackageDialog'

export default {
  name: 'CheckoutCodeInput',
  components: {
    VoucherPackageDialog
  },
  configuration: ['currentSite', 'currentCircuit', 'discounts', 'voucherTypes'],
  context: ['currentUser', 'currentRegisterClient'],
  inject: ['order', 'recaptcha', 'executeRecaptchaAsync'],
  props: {
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    isDrawer: {
      type: Boolean,
      required: false,
      default: false
    },
    checkout: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: false,
      default: i18n.global.t('checkout.actions.addGiftCardVoucherPromoCode')
    }
  },
  emits: ['add-voucher', 'add-voucher-package', 'input-checkout-code'],
  data () {
    return {
      isCheckoutCodeInputActive: false,
      checkoutCodeUid: '',
      lastFetchedCheckoutCodeUid: '',
      checkoutCodeInputLoading: false,
      errorMessage: ''
    }
  },
  computed: {
    computedInputLabel () {
      return this.errorMessage || this.label
    },
    meerkatMovieVoucherType () {
      return this.currentSite.meerkatMovieEnabledValue ? _.find(this.voucherTypes.data, { isMeerkatMovieValue: true }) : null
    }
  },
  methods: {
    async validateCheckoutCode (code, recaptchaToken = null) {
      if (!recaptchaToken && this.currentSite.recaptchaRequired) {
        try {
          recaptchaToken = await this.executeRecaptchaAsync()
        } catch (error) {
          this.resetCodeInput(error.message || 'Recaptcha failed')
          return false
        }
      }

      const checkoutCode = new CheckoutCode({}, {
        queryName: 'validateCheckoutCode',
        voucher: {
          issuedVoucherPackage: {
            vouchers: 'default'
          }
        },
        giftCard: 'default',
        issuedVoucherPackage: {
          vouchers: 'default'
        },
        discount: 'default',
        voucherType: 'default',
        variables: {
          code,
          movieEdiCode: this.getMovieEdiCode(),
          recaptchaToken
        }
      })

      await checkoutCode.fetch(true)

      if (!checkoutCode.valid) {
        const errorMessage = this.processError(checkoutCode.errorCode, checkoutCode.error, checkoutCode.codeType)
        this.resetCodeInput(errorMessage)
        checkoutCode.destroy()
        return false
      }

      // Handle the validated code based on its type
      const success = await this.handleValidatedCode(checkoutCode)

      if (success) {
        this.resetCodeInput(null)
      } else {
        this.checkoutCodeInputLoading = false
      }

      checkoutCode.destroy()
      return success
    },

    async handleValidatedCode (checkoutCode) {
      const { codeType, voucher, giftCard, issuedVoucherPackage, ceaCard, meerkatMovie, discount } = checkoutCode

      switch (_.camelCase(codeType)) {
        case CHECKOUT_CODE_TYPES.VOUCHER:
          if (voucher?.issuedVoucherPackage) {
            this.$refs.voucherPackageDialog.show(voucher.issuedVoucherPackage)
            return true
          } else if (voucher?.uid) {
            Notify.create({
              timeout: 3000,
              message: this.$i18n.t('voucher.applied'),
              type: 'positive',
              position: 'top'
            })
            this.order.applyIndependentVoucher(voucher.clone())
            this.$emit('add-voucher', voucher)
            return true
          }
          break

        case CHECKOUT_CODE_TYPES.GIFT_CARD:
          if (giftCard?.uid) {
            Notify.create({
              timeout: 3000,
              message: this.$i18n.t('giftCard.addedWithValue', { value: this.$n(giftCard.balance, 'currency') }),
              type: 'positive',
              position: 'top'
            })
            this.order.addGiftCard(giftCard.clone())
            return true
          }
          break

        case CHECKOUT_CODE_TYPES.ISSUED_VOUCHER_PACKAGE:
          if (issuedVoucherPackage?.uid) {
            this.$refs.voucherPackageDialog.show(issuedVoucherPackage.clone())
            return true
          }
          break

        case CHECKOUT_CODE_TYPES.CEA_CARD:
          if (ceaCard === true) {
            const isValidCeaOrder = this.order.isValidCeaOrder()
            if (this.order.ceaCardApplied) {
              Notify.create({
                timeout: 3000,
                message: this.$i18n.t('pages.payment.errors.alreadyAppliedCEACard'),
                type: 'negative',
                position: 'top'
              })
              this.resetCodeInput(this.$i18n.t('pages.payment.errors.alreadyAppliedCEACard'))
              return false
            } else if (!isValidCeaOrder) {
              Notify.create({
                timeout: 3000,
                message: this.$i18n.t('pages.payment.errors.invalidCEACard'),
                type: 'negative',
                position: 'top'
              })
              this.resetCodeInput(this.$i18n.t('pages.payment.errors.invalidCEACard'))
              return false
            } else if (_.isNil(this.ceaCardVoucherType())) {
              Notify.create({
                timeout: 3000,
                message: this.$i18n.t('pages.payment.errors.ceaTicketsAreUnavailable'),
                type: 'negative',
                position: 'top'
              })
              this.resetCodeInput(this.$i18n.t('pages.payment.errors.ceaTicketsAreUnavailable'))
              return false
            } else {
              this.order.addIndependentVoucher(new Voucher({ voucherTypeId: this.ceaCardVoucherType().id }))
              Notify.create({
                timeout: 3000,
                message: this.$i18n.t('checkoutCode.errors.ceaCardApplied'),
                type: 'positive',
                position: 'top'
              })
              return true
            }
          }
          break

        case CHECKOUT_CODE_TYPES.MEERKAT_MOVIE:
          if (meerkatMovie === true) {
            if (this.order.hasMeerkatMovieVoucher) {
              Notify.create({
                timeout: 3000,
                message: 'Only one Meerkat Movies voucher can be used per order.',
                type: 'negative',
                position: 'top'
              })
              return false
            }

            this.order.addIndependentVoucher(voucher.clone())
            if (!this.order.hasMeerkatMovieVoucherApplied) {
              Notify.create({
                timeout: 3000,
                message: 'Meerkat Movies voucher requires two tickets to be purchased.',
                type: 'negative',
                position: 'top'
              })
            }
            return true
          }
          break

        case CHECKOUT_CODE_TYPES.DISCOUNT:
          if (!_.isNil(discount)) {
            const configurationDiscount = _.find(this.discounts.data, { id: discount.id })
            if (_.isNil(configurationDiscount)) {
              Notify.create({
                timeout: 3000,
                message: this.$i18n.t('pages.payment.errors.notFound'),
                type: 'negative',
                position: 'top'
              })
              this.resetCodeInput(this.$i18n.t('pages.payment.errors.notFound'))
              return false
            }
            this.order.addIndependentDiscount(configurationDiscount)
            Notify.create({
              timeout: 3000,
              message: this.$i18n.t('discount.applied', { discount: discount.displayName }),
              type: 'positive',
              position: 'top'
            })
            return true
          }
          break

        default:
          this.resetCodeInput((codeType ? `${codeType} ` : '') + this.$i18n.t('pages.payment.errors.notFound'))
          return false
      }

      return false
    },
    processError (errorCode, error, modelType) {
      modelType = _.camelCase(modelType)

      // Get the model name for interpolation
      let modelName = null
      switch (modelType) {
        case 'voucher':
          modelName = this.$i18n.t('voucher.voucher', 1)
          break
        case 'giftCard':
          modelName = this.$i18n.t('giftCard.giftCard', 1)
          break
        case 'issuedVoucherPackage':
          modelName = this.$i18n.t('voucherPackage.voucherPackage', 1)
          break
        case 'discount':
          modelName = this.$i18n.t('pricing.discount', 1)
          break
        default:
          modelName = _.startCase(modelType || '')
      }

      switch (errorCode) {
        case CHECKOUT_CODE_ERROR_CODES.NOT_FOUND:
          return this.$i18n.t('checkoutCode.errors.thingCodeNotFound', { thing: modelName })
        case CHECKOUT_CODE_ERROR_CODES.NOT_VALID:
          return this.$i18n.t('checkoutCode.errors.thingCodeNotValid', { thing: modelName })
        case CHECKOUT_CODE_ERROR_CODES.EXPIRED:
          return this.$i18n.t('checkoutCode.errors.thingCodeExpired', { thing: modelName })
        case CHECKOUT_CODE_ERROR_CODES.REDEEMED:
          return this.$i18n.t('checkoutCode.errors.thingCodeRedeemed', { thing: modelName })
        case CHECKOUT_CODE_ERROR_CODES.LIMIT_REACHED:
          return this.$i18n.t('checkoutCode.errors.limitReached')
        case CHECKOUT_CODE_ERROR_CODES.UNAVAILABLE:
          return this.$i18n.t('checkoutCode.errors.unavailable')
        case CHECKOUT_CODE_ERROR_CODES.INTERNAL_ERROR:
          return this.$i18n.t('checkoutCode.errors.internalError')
        case CHECKOUT_CODE_ERROR_CODES.UNHANDLED_ERROR:
          return error || this.$i18n.t('checkoutCode.errors.thingCodeNotFound', { thing: modelName })
        default:
          return this.$i18n.t('checkoutCode.errors.thingCodeNotFound', { thing: modelName })
      }
    },

    getMovieEdiCode () {
      if (!this.meerkatMovieVoucherType || !this.checkoutCodeUid) return null
      return this.order.meerkatMovieOrderItems[0]?.ticket?.showing?.movie?.dcmEdiMovieId || null
    },

    ceaCardVoucherType () {
      return this.currentSite.ceaCardEnabled ? _.find(this.voucherTypes.data, { isCeaCard: true }) : null
    },
    findCheckoutCodeOnInput () {
      // There is no need to constantly validate the code on the consumer side
      // This just causes a lot of unnecessary requests and is really just unnecessary
      if (!this.$register) return

      if (!this.checkoutCodeUid) {
        this.resetCodeInput(null)
        return
      }

      this.$emit('input-checkout-code', true)

      if (!this._debouncedFindCheckoutCode) {
        this._debouncedFindCheckoutCode = _.debounce(this.findCheckoutCode, 2500) // 2.5 seconds
      }
      this._debouncedFindCheckoutCode()
    },
    findCheckoutCodeOnEnter () {
      this.findCheckoutCode()
    },
    async findCheckoutCode () {
      try {
        if (this.checkoutCodeInputLoading) return
        if (!this.checkoutCodeUid) {
          this.resetCodeInput(null)
          return
        }
        if (this.checkoutCodeUid === this.lastFetchedCheckoutCodeUid) return

        this.checkoutCodeInputLoading = true

        // Use the new validateCheckoutCode query for all code types
        const success = await this.validateCheckoutCode(this.checkoutCodeUid)

        if (success) {
          this.lastFetchedCheckoutCodeUid = this.checkoutCodeUid
        }
      } catch (error) {
        this.resetCodeInput(error)
      } finally {
        // This should have already been triggered by resetCodeInput, but just in case emitting here too
        this.$emit('input-checkout-code', false)
      }
    },
    resetCodeInput (errorMessage) {
      this.checkoutCodeInputLoading = false
      this.$emit('input-checkout-code', false)

      this.errorMessage = errorMessage
      if (!errorMessage) {
        this.checkoutCodeUid = ''
      }
    },
    textColor (color) {
      // when return hex code
      if (color.startsWith('#')) return this.$isColorDark(color) ? 'white' : 'black'
      // when return value from colorOptions
      const colorHex = this.$getSiteColorHex(this.currentSite, color)
      if (!colorHex) return false
      return this.$isColorDark(colorHex) ? 'white' : 'black'
    }
  }
}
</script>
